/**
 * Pure geometry for the standings-over-time line chart.
 *
 * No d3 dependency — just linear scales and SVG path-string builders, following the
 * "helper builds path strings, component renders <path>" split used by knockoutConnectors.ts.
 *
 * The chart is drawn in a fixed viewBox; the SVG element scales responsively. A fractional
 * `visibleThrough` (0..maxMatchday) clips each line at a point partway along its final segment,
 * which is what makes the playback animation grow the lines smoothly.
 */
import type { UserSeries } from './standingsSeries';

export interface ChartMargin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface ChartDims {
  width: number;
  height: number;
  margin: ChartMargin;
}

export interface ChartScales {
  dims: ChartDims;
  maxMatchday: number;
  maxPoints: number;
  innerWidth: number;
  innerHeight: number;
  xScale: (matchday: number) => number;
  yScale: (points: number) => number;
}

export interface Point {
  x: number;
  y: number;
}

export interface AxisTick {
  value: number;
  /** Pixel position along the relevant axis. */
  pos: number;
  label: string;
}

export const DEFAULT_CHART_DIMS: ChartDims = {
  width: 820,
  height: 440,
  margin: { top: 16, right: 64, bottom: 36, left: 44 },
};

/** Round a maximum up to a visually pleasant axis bound (e.g. 50 -> 50, 137 -> 200). */
export function niceCeil(value: number): number {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const normalized = value / magnitude;
  let step: number;
  if (normalized <= 1) step = 1;
  else if (normalized <= 2) step = 2;
  else if (normalized <= 2.5) step = 2.5;
  else if (normalized <= 5) step = 5;
  else step = 10;
  return step * magnitude;
}

export function createChartScales(dims: ChartDims, maxMatchday: number, maxPoints: number): ChartScales {
  const innerWidth = Math.max(1, dims.width - dims.margin.left - dims.margin.right);
  const innerHeight = Math.max(1, dims.height - dims.margin.top - dims.margin.bottom);
  const safeMaxMd = Math.max(1, maxMatchday);
  const safeMaxPts = Math.max(1, maxPoints);

  const xScale = (matchday: number): number => dims.margin.left + (matchday / safeMaxMd) * innerWidth;

  const yScale = (points: number): number => dims.margin.top + innerHeight - (points / safeMaxPts) * innerHeight;

  return { dims, maxMatchday, maxPoints, innerWidth, innerHeight, xScale, yScale };
}

/** Largest finite cumulative score across all series (used to set the y-axis bound). */
export function maxPointsAcross(series: UserSeries[]): number {
  let max = 0;
  for (const s of series) {
    for (const p of s.points) {
      if (p != null && p > max) max = p;
    }
  }
  return max;
}

/**
 * Build the SVG path `d` for one user's line up to `visibleThrough`.
 *
 * Connects consecutive non-null points; `null` gaps (e.g. before a late joiner appears)
 * break the line into separate subpaths so nothing jumps across the gap. A fractional
 * `visibleThrough` interpolates the tip along the segment after the last whole matchday.
 */
export function buildLinePath(series: UserSeries, scales: ChartScales, visibleThrough: number): string {
  const pts = series.points;
  const lastIndex = pts.length - 1;
  const vtFloor = Math.max(0, Math.min(lastIndex, Math.floor(visibleThrough)));
  const frac = Math.max(0, Math.min(1, visibleThrough - vtFloor));

  const segments: Point[][] = [];
  let current: Point[] = [];

  for (let md = 0; md <= vtFloor; md += 1) {
    const value = pts[md];
    if (value == null) {
      if (current.length) {
        segments.push(current);
        current = [];
      }
      continue;
    }
    current.push({ x: scales.xScale(md), y: scales.yScale(value) });
  }

  // Fractional tip: interpolate into the segment heading toward the next whole matchday.
  if (frac > 0 && vtFloor + 1 <= lastIndex) {
    const a = pts[vtFloor];
    const b = pts[vtFloor + 1];
    if (a != null && b != null && current.length) {
      current.push({
        x: scales.xScale(vtFloor + frac),
        y: scales.yScale(a + (b - a) * frac),
      });
    }
  }

  if (current.length) segments.push(current);

  return segments
    .map((seg) => seg.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' '))
    .join(' ');
}

/** Visible whole-matchday vertices for marker dots, up to floor(visibleThrough). */
export function visibleVertices(
  series: UserSeries,
  scales: ChartScales,
  visibleThrough: number,
): Array<{ matchday: number; x: number; y: number; points: number }> {
  const pts = series.points;
  const vtFloor = Math.floor(visibleThrough);
  const result: Array<{ matchday: number; x: number; y: number; points: number }> = [];
  for (let md = 0; md <= Math.min(pts.length - 1, vtFloor); md += 1) {
    const value = pts[md];
    if (value == null) continue;
    result.push({ matchday: md, x: scales.xScale(md), y: scales.yScale(value), points: value });
  }
  return result;
}

/** The current tip of a line (for end labels), accounting for the fractional playback head. */
export function lineTip(
  series: UserSeries,
  scales: ChartScales,
  visibleThrough: number,
): (Point & { points: number }) | null {
  const pts = series.points;
  const lastIndex = pts.length - 1;
  const vtFloor = Math.max(0, Math.min(lastIndex, Math.floor(visibleThrough)));
  const frac = Math.max(0, Math.min(1, visibleThrough - vtFloor));

  if (frac > 0 && vtFloor + 1 <= lastIndex) {
    const a = pts[vtFloor];
    const b = pts[vtFloor + 1];
    if (a != null && b != null) {
      const value = a + (b - a) * frac;
      return { x: scales.xScale(vtFloor + frac), y: scales.yScale(value), points: value };
    }
  }

  for (let md = vtFloor; md >= 0; md -= 1) {
    const value = pts[md];
    if (value != null) {
      return { x: scales.xScale(md), y: scales.yScale(value), points: value };
    }
  }
  return null;
}

/** Evenly spaced matchday ticks (1..maxMatchday), thinned so labels do not collide. */
export function matchdayTicks(scales: ChartScales): AxisTick[] {
  const ticks: AxisTick[] = [];
  const { maxMatchday } = scales;
  if (maxMatchday < 1) return ticks;
  // Aim for <= ~17 labels; step up for longer tournaments.
  const step = maxMatchday <= 17 ? 1 : Math.ceil(maxMatchday / 17);
  for (let md = 1; md <= maxMatchday; md += 1) {
    if (md % step === 0 || md === maxMatchday) {
      ticks.push({ value: md, pos: scales.xScale(md), label: String(md) });
    }
  }
  return ticks;
}

/** Round point-axis ticks from 0..niceCeil(maxPoints). */
export function pointTicks(scales: ChartScales, count = 5): AxisTick[] {
  const top = niceCeil(scales.maxPoints);
  const ticks: AxisTick[] = [];
  for (let i = 0; i <= count; i += 1) {
    const value = (top / count) * i;
    ticks.push({ value, pos: scales.yScale(value), label: String(Math.round(value)) });
  }
  return ticks;
}
